import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, CheckCircle, Info, AlertTriangle, X } from 'lucide-react';
import { useAppSelector } from '../../store/hooks';
import { useAlert, AlertType } from '../../contexts/AlertContext';
import Button from './Button';

const AlertModal: React.FC = () => {
  const { theme } = useAppSelector((state) => state.ui);
  const { alertState } = useAlert();

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && alertState.isOpen) {
        if (alertState.isConfirm && alertState.onCancel) {
          alertState.onCancel();
        } else if (!alertState.isConfirm && alertState.onConfirm) {
          alertState.onConfirm();
        }
      }
    };

    if (alertState.isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [alertState]);

  // Handle Enter key for confirm
  useEffect(() => {
    const handleEnter = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && alertState.isOpen && !alertState.isConfirm && alertState.onConfirm) {
        event.preventDefault();
        alertState.onConfirm();
      }
    };

    if (alertState.isOpen) {
      document.addEventListener('keydown', handleEnter);
    }

    return () => {
      document.removeEventListener('keydown', handleEnter);
    };
  }, [alertState]);

  if (!alertState.isOpen) return null;

  const getIconConfig = (type: AlertType) => {
    const configs = {
      success: {
        icon: CheckCircle,
        iconColor: 'text-green-400',
        bgColor: 'bg-green-100 dark:bg-green-900/30',
        borderColor: 'border-green-200 dark:border-green-800',
      },
      error: {
        icon: AlertCircle,
        iconColor: 'text-red-400',
        bgColor: 'bg-red-100 dark:bg-red-900/30',
        borderColor: 'border-red-200 dark:border-red-800',
      },
      warning: {
        icon: AlertTriangle,
        iconColor: 'text-yellow-400',
        bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
        borderColor: 'border-yellow-200 dark:border-yellow-800',
      },
      info: {
        icon: Info,
        iconColor: 'text-blue-400',
        bgColor: 'bg-blue-100 dark:bg-blue-900/30',
        borderColor: 'border-blue-200 dark:border-blue-800',
      },
    };
    return configs[type];
  };

  const iconConfig = getIconConfig(alertState.type);
  const Icon = iconConfig.icon;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      if (alertState.isConfirm && alertState.onCancel) {
        alertState.onCancel();
      } else if (!alertState.isConfirm && alertState.onConfirm) {
        alertState.onConfirm();
      }
    }
  };

  const getConfirmButtonVariant = (): 'primary' | 'danger' => {
    if (alertState.type === 'error') return 'danger';
    return 'primary';
  };

  const modalContent = (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="alert-title"
      aria-describedby="alert-message"
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity" />

      {/* Modal */}
      <div className="relative w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div
          className={`relative w-full transform overflow-hidden rounded-lg shadow-xl transition-all ${
            theme === 'dark'
              ? 'bg-gray-900/95 border border-gray-700/50'
              : 'bg-white border border-gray-200/50'
          }`}
        >
          {/* Header */}
          <div
            className={`flex items-start gap-4 px-6 py-5 border-b ${
              theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}
          >
            <div
              className={`flex-shrink-0 flex items-center justify-center rounded-full p-2 ${iconConfig.bgColor}`}
            >
              <Icon className={`h-6 w-6 ${iconConfig.iconColor}`} />
            </div>

            <div className="flex-1 min-w-0">
              {alertState.title && (
                <h3
                  id="alert-title"
                  className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}
                >
                  {alertState.title}
                </h3>
              )}
              <p
                id="alert-message"
                className={`mt-1 text-sm whitespace-pre-wrap ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                }`}
              >
                {alertState.message}
              </p>
            </div>

            {!alertState.isConfirm && (
              <button
                onClick={alertState.onConfirm}
                className={`flex-shrink-0 p-1.5 rounded-lg transition-colors ${
                  theme === 'dark'
                    ? 'hover:bg-gray-700 text-gray-400 hover:text-white'
                    : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                }`}
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Actions */}
          <div
            className={`px-6 py-4 flex items-center justify-end gap-3 ${
              theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            } ${alertState.isConfirm ? 'border-t' : ''}`}
          >
            {alertState.isConfirm ? (
              <>
                <Button variant="secondary" onClick={alertState.onCancel}>
                  {alertState.cancelText || 'Cancel'}
                </Button>
                <Button variant={getConfirmButtonVariant()} onClick={alertState.onConfirm}>
                  {alertState.confirmText || 'Confirm'}
                </Button>
              </>
            ) : (
              <Button variant="primary" onClick={alertState.onConfirm} autoFocus>
                OK
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default AlertModal;

